import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Tier } from '../types';
import tierService from '../services/tierService';
import { Field, Input, DateInput, Button, FormActions } from './ui';

const d = (iso?: string) => (iso ? iso.split('T')[0] : ''); // ISO -> yyyy-mm-dd für date-Inputs

/**
 * Tier anlegen/bearbeiten, inline eingebettet. Geteilt von Grab-Detailseite und Kunden-Detailseite
 * (dort direkt aus dem Vertragsformular heraus, damit das Tier nicht erst woanders angelegt werden
 * muss). `tier` = undefined → Anlegen. Speichert selbst und gibt das gespeicherte Tier zurück.
 * Nicht in ein anderes <form> schachteln – verschachtelte Formulare sind in HTML ungültig.
 */
const TierFormular: React.FC<{
  grabId: number;
  tier?: Tier;
  onSaved: (tier: Tier) => void;
  onCancel: () => void;
}> = ({ grabId, tier, onSaved, onCancel }) => {
  const isNew = !tier;
  const [form, setForm] = useState<any>(tier
    ? { name: tier.name || '', art: tier.art || '', rasse: tier.rasse || '', gebDatum: d(tier.gebDatum), sterbeDatum: d(tier.sterbeDatum), gewicht: tier.gewicht ?? '', groesse: tier.groesse || '', beisetzungsdatum: d(tier.beisetzungsdatum) }
    : { name: '', art: '', rasse: '', gebDatum: '', sterbeDatum: '', gewicht: '', groesse: '', beisetzungsdatum: '' });
  const [saving, setSaving] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name || undefined, art: form.art || undefined, rasse: form.rasse || undefined,
      gebDatum: form.gebDatum || undefined, sterbeDatum: form.sterbeDatum || undefined,
      gewicht: form.gewicht !== '' && form.gewicht != null ? parseFloat(form.gewicht) : undefined,
      groesse: form.groesse || undefined, beisetzungsdatum: form.beisetzungsdatum || undefined,
    };
    try {
      const gespeichert = tier
        ? await tierService.update(tier.id, { ...payload, grabId, active: true })
        : await tierService.create({ ...payload, grabId });
      onSaved(gespeichert);
    } catch (err: any) { toast.error(err?.response?.data?.error || 'Tier konnte nicht gespeichert werden'); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Name"><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></Field>
        <Field label="Art"><Input value={form.art} onChange={e => setForm({ ...form, art: e.target.value })} placeholder="Hund, Katze, …" /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Rasse"><Input value={form.rasse} onChange={e => setForm({ ...form, rasse: e.target.value })} /></Field>
        <Field label="Größe"><Input value={form.groesse} onChange={e => setForm({ ...form, groesse: e.target.value })} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Geburtsdatum"><DateInput value={form.gebDatum} onChange={v => setForm({ ...form, gebDatum: v })} /></Field>
        <Field label="Sterbedatum"><DateInput value={form.sterbeDatum} onChange={v => setForm({ ...form, sterbeDatum: v })} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Gewicht (kg)"><Input type="number" step="0.01" value={form.gewicht} onChange={e => setForm({ ...form, gewicht: e.target.value })} /></Field>
        <Field label="Beisetzung"><DateInput value={form.beisetzungsdatum} onChange={v => setForm({ ...form, beisetzungsdatum: v })} /></Field>
      </div>
      {isNew && <p className="rounded-lg bg-brand-50 px-3 py-2 text-xs text-brand-800">Beim Anlegen wird das Grab automatisch auf „Belegt“ gesetzt. Mehrere Tiere pro Grab sind möglich.</p>}
      <FormActions>
        <Button type="button" variant="secondary" onClick={onCancel}>Abbrechen</Button>
        <Button type="submit" loading={saving}>{isNew ? 'Tier anlegen' : 'Speichern'}</Button>
      </FormActions>
    </form>
  );
};

export default TierFormular;
